import { supabase } from '../../../lib/supabase';
import type { Purchase, PurchaseFormData } from '../types';

export const purchaseService = {
  async getAll(): Promise<Purchase[]> {
    const { data, error } = await supabase
      .from('purchases')
      .select(`
        *,
        supplier:suppliers(id, name),
        items:purchase_items(*)
      `)
      .order('date', { ascending: false });

    if (error) throw error;

    return (data || []).map(purchase => ({
      id: purchase.id,
      invoiceNo: purchase.invoice_no,
      supplierId: purchase.supplier_id,
      date: purchase.date,
      items: purchase.items.map((item: any) => ({
        medicineId: item.medicine_id,
        batchNo: item.batch_no,
        expiryDate: item.expiry_date,
        qty: item.qty,
        unitPrice: item.unit_price,
        mrp: item.mrp || 0,
      })),
      subtotal: purchase.subtotal,
      discount: purchase.discount || 0,
      tax: purchase.tax || 0,
      total: purchase.total,
      notes: purchase.notes,
    }));
  },

  async getById(id: string): Promise<Purchase | null> {
    const { data, error } = await supabase
      .from('purchases')
      .select(`
        *,
        supplier:suppliers(id, name),
        items:purchase_items(*)
      `)
      .eq('id', id)
      .single();

    if (error) return null;
    if (!data) return null;

    return {
      id: data.id,
      invoiceNo: data.invoice_no,
      supplierId: data.supplier_id,
      date: data.date,
      items: data.items.map((item: any) => ({
        medicineId: item.medicine_id,
        batchNo: item.batch_no,
        expiryDate: item.expiry_date,
        qty: item.qty,
        unitPrice: item.unit_price,
        mrp: item.mrp || 0,
      })),
      subtotal: data.subtotal,
      discount: data.discount || 0,
      tax: data.tax || 0,
      total: data.total,
      notes: data.notes,
    };
  },

  async create(data: PurchaseFormData): Promise<Purchase> {
    const subtotal = data.items.reduce((sum, item) => sum + (item.qty * item.unitPrice), 0);
    const discount = data.discount || 0;
    const tax = data.tax || 0;
    const total = subtotal - discount + tax;

    const { data: purchase, error: purchaseError } = await supabase
      .from('purchases')
      .insert({
        invoice_no: data.invoiceNo,
        supplier_id: data.supplierId,
        date: data.date,
        subtotal,
        discount,
        tax,
        total,
        notes: data.notes,
      })
      .select()
      .single();

    if (purchaseError) throw purchaseError;

    const { error: itemsError } = await supabase
      .from('purchase_items')
      .insert(
        data.items.map(item => ({
          purchase_id: purchase.id,
          medicine_id: item.medicineId,
          batch_no: item.batchNo,
          expiry_date: item.expiryDate,
          qty: item.qty,
          unit_price: item.unitPrice,
          mrp: item.mrp || 0,
        }))
      );

    if (itemsError) throw itemsError;

    return {
      id: purchase.id,
      invoiceNo: purchase.invoice_no,
      supplierId: purchase.supplier_id,
      date: purchase.date,
      items: data.items,
      subtotal,
      discount,
      tax,
      total,
      notes: data.notes,
    };
  },

  async getRecent(limit: number = 10): Promise<Purchase[]> {
    const { data, error } = await supabase
      .from('purchases')
      .select(`
        *,
        supplier:suppliers(id, name),
        items:purchase_items(*)
      `)
      .order('date', { ascending: false })
      .limit(limit);

    if (error) throw error;

    return (data || []).map(purchase => ({
      id: purchase.id,
      invoiceNo: purchase.invoice_no,
      supplierId: purchase.supplier_id,
      date: purchase.date,
      items: purchase.items.map((item: any) => ({
        medicineId: item.medicine_id,
        batchNo: item.batch_no,
        expiryDate: item.expiry_date,
        qty: item.qty,
        unitPrice: item.unit_price,
        mrp: item.mrp || 0,
      })),
      subtotal: purchase.subtotal,
      discount: purchase.discount || 0,
      tax: purchase.tax || 0,
      total: purchase.total,
      notes: purchase.notes,
    }));
  },

  async getByDateRange(startDate: string, endDate: string): Promise<Purchase[]> {
    const { data, error } = await supabase
      .from('purchases')
      .select(`
        *,
        supplier:suppliers(id, name),
        items:purchase_items(*)
      `)
      .gte('date', startDate)
      .lte('date', endDate)
      .order('date', { ascending: false });

    if (error) throw error;

    return (data || []).map(purchase => ({
      id: purchase.id,
      invoiceNo: purchase.invoice_no,
      supplierId: purchase.supplier_id,
      date: purchase.date,
      items: purchase.items.map((item: any) => ({
        medicineId: item.medicine_id,
        batchNo: item.batch_no,
        expiryDate: item.expiry_date,
        qty: item.qty,
        unitPrice: item.unit_price,
        mrp: item.mrp || 0,
      })),
      subtotal: purchase.subtotal,
      discount: purchase.discount || 0,
      tax: purchase.tax || 0,
      total: purchase.total,
      notes: purchase.notes,
    }));
  }
};
